
import { useEffect, useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { supabase } from '../../lib/supabase';

interface MembroEquipa {
  id: string;
  nome: string;
  cargo: string;
  foto_url: string;
  descricao: string;
  ordem: number;
  ativo: boolean;
}

const EquipaPage = () => {
  const [membros, setMembros] = useState<MembroEquipa[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    carregarMembros();
  }, []);

  const carregarMembros = async () => {
    try {
      // Carregar membros da tabela equipa_site (controlada pelo admin)
      const { data, error } = await supabase
        .from('equipa_site')
        .select('*')
        .eq('ativo', true)
        .order('ordem', { ascending: true });

      if (error) throw error;
      setMembros(data || []);
    } catch (error) {
      console.error('Erro ao carregar membros:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-luxury-black">
      <Header />

      <main className="pt-32 pb-24">
        {/* Hero */}
        <section className="px-8 lg:px-20 mb-24 text-center">
          <h1 className="text-white text-5xl lg:text-7xl font-serif font-bold mb-6">
            A Nossa <span className="bg-gradient-gold bg-clip-text text-transparent">Equipa</span>
          </h1>
          <p className="text-gray-400 text-lg lg:text-xl max-w-3xl mx-auto">
            Conheça os profissionais que tornam a Dindimcast possível
          </p>
        </section>

        {/* Membros da Equipa */}
        <section className="px-8 lg:px-20 mb-24">
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-luxury-gold"></div>
            </div>
          ) : membros.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {membros.map((membro, index) => (
                <div
                  key={membro.id}
                  className="bg-gradient-dark border border-luxury-gold/20 rounded-2xl p-8 hover:border-luxury-gold transition-all duration-300 hover:shadow-gold animate-fade-in-up text-center"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="w-32 h-32 mx-auto mb-6 rounded-full overflow-hidden border-4 border-luxury-gold/30">
                    <img
                      src={membro.foto_url || `https://public.readdy.ai/ai/img_res/b4261a5d26d63c125cadec22f618117e.jpg${index}&orientation=squarish`}
                      alt={membro.nome}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <h3 className="text-luxury-gold text-2xl font-bold mb-2">
                    {membro.nome}
                  </h3>
                  <p className="text-gray-400 mb-4">
                    {membro.cargo}
                  </p>
                  {membro.descricao && (
                    <p className="text-gray-500 text-sm mb-4">
                      {membro.descricao}
                    </p>
                  )}
                  <div className="flex justify-center gap-4">
                    <a
                      href="#"
                      className="w-10 h-10 flex items-center justify-center bg-luxury-gold/10 rounded-full hover:bg-luxury-gold hover:text-black transition-all cursor-pointer"
                    >
                      <i className="ri-linkedin-fill"></i>
                    </a>
                    <a
                      href="#"
                      className="w-10 h-10 flex items-center justify-center bg-luxury-gold/10 rounded-full hover:bg-luxury-gold hover:text-black transition-all cursor-pointer"
                    >
                      <i className="ri-twitter-fill"></i>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <i className="ri-team-line text-6xl text-gray-700 mb-4"></i>
              <p className="text-gray-400">Brevemente...</p>
            </div>
          )}
        </section>

        {/* Formulário de Candidatura */}
        <section className="px-8 lg:px-20">
          <div className="max-w-3xl mx-auto bg-gradient-dark border border-luxury-gold/20 rounded-2xl p-12">
            <h2 className="text-white text-4xl font-serif font-bold text-center mb-4">
              Junte-se à <span className="text-luxury-gold">Nossa Equipa</span>
            </h2>
            <p className="text-gray-400 text-center mb-8">
              Envie a sua candidatura e faça parte desta jornada
            </p>

            <form
              id="candidatura-form"
              data-readdy-form
              action="https://readdy.ai/api/form/d4t00s15bv6k73lc3nug"
              method="POST"
              className="space-y-6"
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const formData = new FormData(form);
                
                const data: Record<string, string> = {};
                formData.forEach((value, key) => {
                  data[key] = value.toString();
                });

                setIsSubmitting(true);
                setSubmitStatus('idle');

                try {
                  const response = await fetch(form.action, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: new URLSearchParams(data).toString(),
                  });

                  if (response.ok) {
                    setSubmitStatus('success');
                    form.reset();
                  } else {
                    setSubmitStatus('error');
                  }
                } catch (error) {
                  setSubmitStatus('error');
                } finally {
                  setIsSubmitting(false);
                }
              }}
            >
              <input type="hidden" name="formId" value="candidatura-equipa-dindimcast" />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-300 mb-2 text-sm">Nome Completo</label>
                  <input
                    type="text"
                    name="nome"
                    required
                    className="w-full px-4 py-3 bg-luxury-black border border-luxury-gold/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-luxury-gold transition-colors"
                    placeholder="O seu nome"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 mb-2 text-sm">Email</label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 bg-luxury-black border border-luxury-gold/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-luxury-gold transition-colors"
                    placeholder="seuemail@exemplo.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-300 mb-2 text-sm">Telefone</label>
                  <input
                    type="tel"
                    name="telefone"
                    required
                    className="w-full px-4 py-3 bg-luxury-black border border-luxury-gold/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-luxury-gold transition-colors"
                    placeholder="+351 000 000 000"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 mb-2 text-sm">Cargo Pretendido</label>
                  <input
                    type="text"
                    name="cargo"
                    required
                    className="w-full px-4 py-3 bg-luxury-black border border-luxury-gold/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-luxury-gold transition-colors"
                    placeholder="Ex: Designer, Desenvolvedor"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-300 mb-2 text-sm">Mensagem / Apresentação</label>
                <textarea
                  name="mensagem"
                  rows={6}
                  maxLength={500}
                  required
                  className="w-full px-4 py-3 bg-luxury-black border border-luxury-gold/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-luxury-gold transition-colors resize-none"
                  placeholder="Conte-nos sobre a sua experiência e motivação..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">Máximo 500 caracteres</p>
              </div>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-500/20 border border-green-500 rounded-lg text-green-400 text-sm">
                  Candidatura enviada com sucesso! Entraremos em contacto em breve.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-400 text-sm">
                  Erro ao enviar candidatura. Por favor, tente novamente.
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-gradient-gold text-black font-bold rounded-lg hover:shadow-gold transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? 'A enviar...' : 'Enviar Candidatura'}
              </button>
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default EquipaPage;
